import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, X, Upload, Loader2, FileText, PlayCircle } from "lucide-react";
import { api, resolveImageUri } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { type: "blog", title: "", content: "", cover_image: "", video_url: "", youtube_url: "" };

function PostForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const uploadCover = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading("cover");
    try { const r = await api.uploadImage(file); setF((s) => ({ ...s, cover_image: r.image_url })); toast.success("Cover uploaded"); }
    catch (err) { toast.error(err.message); } finally { setUploading(""); }
  };
  const uploadVideo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading("video");
    try { const r = await api.uploadVideo(file); setF((s) => ({ ...s, video_url: r.video_url })); toast.success("Video uploaded"); }
    catch (err) { toast.error(err.message); } finally { setUploading(""); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      if (initial?.id) await api.updatePost(initial.id, f);
      else await api.createPost(f);
      toast.success("Post saved");
      onSaved();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const inputCls = "w-full h-11 rounded-lg border border-line px-3 text-sm focus:outline-none focus:border-brand";

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-paper rounded-2xl w-full max-w-xl my-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-3xl text-ink">{initial?.id ? "Edit" : "New"} Post</h2>
          <button onClick={onClose} className="p-1 text-muted2"><X size={22} /></button>
        </div>
        <form onSubmit={save} className="space-y-4">
          <div className="flex gap-2">
            {["blog", "vlog"].map((t) => (
              <button type="button" key={t} onClick={() => setF((s) => ({ ...s, type: t }))} data-testid={`post-type-${t}`} className={`h-10 px-4 rounded-full text-sm font-semibold border flex items-center gap-1.5 ${f.type === t ? "bg-brand text-white border-brand" : "border-line text-muted2"}`}>
                {t === "blog" ? <FileText size={15} /> : <PlayCircle size={15} />} {t === "blog" ? "Text Blog" : "Video Vlog"}
              </button>
            ))}
          </div>
          <input value={f.title} onChange={set("title")} placeholder="Title" className={inputCls} data-testid="post-title-input" />

          {f.type === "blog" ? (
            <>
              <textarea value={f.content} onChange={set("content")} rows={8} placeholder="Write your blog…" className="w-full rounded-lg border border-line px-3 py-2 text-sm focus:outline-none focus:border-brand" data-testid="post-content-input" />
              <div className="flex items-center gap-3">
                {f.cover_image && <img src={resolveImageUri(f.cover_image)} alt="" className="w-16 h-16 rounded-lg object-cover" />}
                <label className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-line text-sm font-semibold cursor-pointer"><Upload size={16} /> {uploading === "cover" ? "Uploading…" : "Cover image"}<input type="file" accept="image/*" className="hidden" onChange={uploadCover} /></label>
              </div>
            </>
          ) : (
            <>
              <input value={f.youtube_url} onChange={set("youtube_url")} placeholder="YouTube link (optional)" className={inputCls} data-testid="post-youtube-input" />
              <div className="flex items-center gap-3">
                {f.video_url && <span className="text-xs text-ok">Video uploaded ✓</span>}
                <label className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-line text-sm font-semibold cursor-pointer"><Upload size={16} /> {uploading === "video" ? "Uploading…" : "Upload video (≤50MB)"}<input type="file" accept="video/*" className="hidden" onChange={uploadVideo} /></label>
              </div>
            </>
          )}

          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 h-12 rounded-full border border-line font-semibold">Cancel</button>
            <button disabled={saving} className="flex-1 h-12 rounded-full bg-brand text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="post-save">{saving ? <Loader2 className="animate-spin" size={18} /> : "Save post"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function BlogsAdmin() {
  const qc = useQueryClient();
  const { data: posts = [] } = useQuery({ queryKey: ["posts"], queryFn: () => api.listPosts() });
  const [editing, setEditing] = useState(null);
  const remove = async (id) => { if (!window.confirm("Delete this post?")) return; try { await api.deletePost(id); toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["posts"] }); } catch (err) { toast.error(err.message); } };
  const saved = () => { setEditing(null); qc.invalidateQueries({ queryKey: ["posts"] }); };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl text-ink">Blogs & Vlogs ({posts.length})</h2>
        <button onClick={() => setEditing({})} className="h-10 px-4 rounded-full bg-brand text-white font-semibold text-sm flex items-center gap-1.5" data-testid="add-post-button"><Plus size={16} /> New post</button>
      </div>
      <div className="space-y-2">
        {posts.map((p) => (
          <div key={p.id} className="flex items-center gap-3 rounded-xl border border-line p-3" data-testid={`admin-post-${p.id}`}>
            <span className="text-[11px] font-semibold uppercase text-brand w-10">{p.type}</span>
            <p className="flex-1 font-medium text-ink text-sm truncate">{p.title}</p>
            <button onClick={() => setEditing(p)} className="p-2 text-muted2 hover:text-brand"><Pencil size={16} /></button>
            <button onClick={() => remove(p.id)} className="p-2 text-muted2 hover:text-err"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      {editing && <PostForm initial={editing} onClose={() => setEditing(null)} onSaved={saved} />}
    </div>
  );
}
